const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

const SHORT_COMPLETIONS = /^(yes|no|okay|ok|sure|exactly|absolutely|i agree|that makes sense|that'?s right|you answered my question)[.!?]*$/i;
const TRAILING_CONTINUATIONS = /\b(is|are|was|were|because|if|when|while|but|and|or|so|that|to|with|about|for|from|then|also)[.!?…]*$/i;
const OPENING_CONTINUATIONS = /^(i think|i feel|i guess|i was wondering|my concern is|my question is|one thing that worries me|if i inherited|so what happened was|the reason is|what i mean is)\b/i;

export const classifyTranscriptCompletion = (transcript = '') => {
  const text = transcript.trim();
  if (!text) return { status: 'unknown', confidence: 0, reason: 'empty' };
  if (SHORT_COMPLETIONS.test(text)) return { status: 'complete', confidence: 0.95, reason: 'short_completion' };
  if (/\.{3}$|…$/.test(text)) return { status: 'incomplete', confidence: 0.95, reason: 'ellipsis' };
  if (TRAILING_CONTINUATIONS.test(text)) return { status: 'incomplete', confidence: 0.9, reason: 'trailing_connector' };

  const words = text.replace(/[.!?…]/g, '').split(/\s+/).filter(Boolean);
  if (OPENING_CONTINUATIONS.test(text) && words.length <= 12) {
    return { status: 'incomplete', confidence: 0.82, reason: 'continuation_opening' };
  }
  if (/[.!?]["')\]]*$/.test(text) && words.length >= 3) return { status: 'complete', confidence: 0.78, reason: 'terminal_punctuation' };
  return { status: 'uncertain', confidence: 0.5, reason: 'ambiguous' };
};

export const calculateAdaptiveTurnDecision = ({
  activeSpeechMs,
  elapsedMs,
  silenceMs,
  burstCount,
  resumedPauses = [],
  recentVoiceEnergy = 0,
  currentEnergy = 0,
  semanticHint = null,
  probePending = false,
  speakingRateWpm = null,
}) => {
  const pauseAverage = resumedPauses.length
    ? resumedPauses.reduce((total, pause) => total + pause, 0) / resumedPauses.length
    : 0;
  const speechRatio = activeSpeechMs / Math.max(1, elapsedMs);
  const shortAnswer = activeSpeechMs <= 900 && burstCount <= 1;
  const longForm = activeSpeechMs >= 3500 || elapsedMs >= 6000;
  let thresholdMs = shortAnswer ? 760 : 980;

  if (activeSpeechMs >= 1800) thresholdMs += 180;
  if (activeSpeechMs >= 4000) thresholdMs += 220;
  if (activeSpeechMs >= 8000) thresholdMs += 180;
  if (speechRatio < 0.72 && elapsedMs > 2000) thresholdMs += 180;
  if (speakingRateWpm && speakingRateWpm < 105) thresholdMs += 220;
  if (speakingRateWpm && speakingRateWpm > 165 && burstCount <= 1) thresholdMs -= 140;
  if (!speakingRateWpm && speechRatio > 0.88 && burstCount <= 1 && activeSpeechMs < 3500) thresholdMs -= 220;
  if (resumedPauses.length) thresholdMs += Math.min(360, 100 + resumedPauses.length * 70 + pauseAverage * 0.18);
  if (semanticHint?.status === 'incomplete') thresholdMs = Math.max(thresholdMs, 1800);
  if (semanticHint?.status === 'complete' && semanticHint.confidence >= 0.75 && !resumedPauses.length) thresholdMs = Math.min(thresholdMs, shortAnswer ? 720 : 920);
  if (probePending) thresholdMs = Math.max(thresholdMs, 1550);
  thresholdMs = Math.round(clamp(thresholdMs, 700, 1900));

  const graceMs = longForm || resumedPauses.length ? 450 : 320;
  const candidateAtMs = Math.max(420, thresholdMs - graceMs);
  const energyDecay = recentVoiceEnergy > 0 ? clamp(1 - currentEnergy / recentVoiceEnergy, 0, 1) : 0;
  let confidence = clamp(silenceMs / thresholdMs, 0, 1.35) * 0.7;
  confidence += energyDecay * 0.12;
  if (shortAnswer) confidence += 0.12;
  if (semanticHint?.status === 'complete') confidence += 0.16 * semanticHint.confidence;
  if (semanticHint?.status === 'incomplete') confidence -= 0.15 * semanticHint.confidence;
  confidence = clamp(confidence, 0, 1);

  const hardMaximumReached = silenceMs >= 2400;
  const shouldEnd = hardMaximumReached || (
    silenceMs >= thresholdMs
    && confidence >= 0.8
    && !probePending
  );
  const shouldProbe = !shortAnswer
    && activeSpeechMs >= 1200
    && silenceMs >= candidateAtMs
    && !semanticHint
    && !probePending;

  return {
    thresholdMs,
    graceMs,
    candidateAtMs,
    confidence,
    shouldProbe,
    shouldEnd,
    hardMaximumReached,
    shortAnswer,
    longForm,
    speechRatio,
    pauseAverage,
    energyDecay,
    speakingRateWpm,
  };
};

export const createAdaptiveTurnTracker = () => {
  const state = {
    voiceStartedAt: 0,
    lastVoiceAt: 0,
    lastFrameAt: 0,
    activeSpeechMs: 0,
    burstCount: 0,
    resumedPauses: [],
    candidateStartedAt: 0,
    speechRevision: 0,
    recentVoiceEnergy: 0,
    semanticHint: null,
    probePending: false,
  };

  const observe = ({ now, speaking, energy }) => {
    const frameDuration = state.lastFrameAt ? Math.min(50, now - state.lastFrameAt) : 0;
    state.lastFrameAt = now;
    let resumed = false;
    let detected = false;
    if (speaking) {
      if (!state.voiceStartedAt) {
        state.voiceStartedAt = now;
        state.burstCount = 1;
        state.speechRevision += 1;
        detected = true;
      } else if (state.lastVoiceAt && now - state.lastVoiceAt >= 220) {
        const pauseMs = now - state.lastVoiceAt;
        state.resumedPauses.push(pauseMs);
        if (state.resumedPauses.length > 6) state.resumedPauses.shift();
        state.burstCount += 1;
        state.speechRevision += 1;
        resumed = true;
      }
      state.lastVoiceAt = now;
      state.activeSpeechMs += frameDuration;
      state.recentVoiceEnergy = state.recentVoiceEnergy ? state.recentVoiceEnergy * 0.88 + energy * 0.12 : energy;
      state.candidateStartedAt = 0;
      state.semanticHint = resumed ? null : state.semanticHint;
      state.probePending = resumed ? false : state.probePending;
    }
    return { detected, resumed, state: { ...state, resumedPauses: [...state.resumedPauses] } };
  };

  return {
    observe,
    setCandidate: (at) => { if (!state.candidateStartedAt) state.candidateStartedAt = at; },
    setSemanticHint: (hint) => { state.semanticHint = hint; state.probePending = false; },
    setProbePending: (pending) => { state.probePending = pending; },
    getSnapshot: () => ({ ...state, resumedPauses: [...state.resumedPauses] }),
  };
};